import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Check, Clock } from "lucide-react";
import { api } from "@/lib/config";

const TIPI_INTERVENTO = {
  parziale: { label: "Con Fornitura del committente" },
  completa: { label: "Completa (chiavi in mano)" },
  lusso: { label: "Alto livello / lusso" },
};

const IMMOBILI = ["Appartamento", "Villa / Casa indipendente"];

const EXTRAS = [
  { id: "elettrico", label: "Impianto elettrico" },
  { id: "idraulico", label: "Impianto idraulico" },
  { id: "riscaldamento", label: "Riscaldamento a pavimento" },
  { id: "infissi", label: "Sostituzione infissi" },
  { id: "climatizzazione", label: "Climatizzazione" },
  { id: "domotica", label: "Domotica" },
];

const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function QuoteCalculator() {
  const [immobile, setImmobile] = useState("Appartamento");
  const [metratura, setMetratura] = useState([100]);
  const [intervento, setIntervento] = useState("parziale");
  const [bagni, setBagni] = useState([1]);
  const [cucina, setCucina] = useState(true);
  const [extras, setExtras] = useState(["elettrico", "idraulico"]);
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [letto, setLetto] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleExtra = (id) =>
    setExtras((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const { min, max, mesi } = useMemo(() => {
    const mq = metratura[0];
    const has = (id) => extras.includes(id);
    const calc = (isMax) => {
      const [bMin, bMax] = intervento === "parziale" ? [90, 120] : [150, 350];
      let t = mq * (isMax ? bMax : bMin);
      if (has("elettrico")) t += isMax ? 3000 + 150 * Math.floor(mq / 10) : 2000 + 100 * Math.floor(mq / 10);
      if (has("idraulico")) t += isMax ? 2000 + 13 * mq : 1500 + 10 * mq;
      t += bagni[0] * (isMax ? 2500 + 15 * mq : 1000 + 10 * mq);
      if (cucina) t += mq * (isMax ? 17 : 12);
      if (has("climatizzazione")) t += 300 * (isMax ? Math.ceil(mq / 50) : Math.floor(mq / 50));
      if (has("infissi")) t += (mq / 10) * (isMax ? 1.8 : 1.2) * 400;
      if (has("domotica")) t += 4000;
      if (has("riscaldamento")) t += 5000;
      if (intervento === "lusso") t *= 1.2;
      if (immobile.startsWith("Villa")) t *= 1.1;
      return Math.round(t / 100) * 100;
    };

    // Durata cantiere stimata (valore singolo, in mesi)
    // Anchor: 100mq + cucina + elettrico + idraulico (fornitura committente) = 4 mesi
    //         50mq stessa dotazione = 2 mesi · 400mq con tutti gli extra = 10 mesi
    let bs = mq <= 100 ? 0.9 + (mq - 50) * 0.04 : 2.9 + (mq - 100) * 0.0153333;
    if (bs < 0.4) bs = 0.4;
    let opt = 0;
    opt += cucina ? 0.4 : 0;
    opt += extras.length * 0.35;
    opt += Math.max(0, bagni[0] - 1) * 0.5; // +2 settimane per ogni bagno in più
    opt += intervento === "lusso" ? 0.6 : intervento === "completa" ? 0.3 : 0;
    const mesiVal = Math.round((bs + opt) * 2) / 2;

    return {
      min: calc(false),
      max: calc(true),
      mesi: mesiVal,
    };
  }, [immobile, metratura, intervento, bagni, cucina, extras]);

  const fmtMesi = (n) => (Number.isInteger(n) ? `${n}` : `${Math.floor(n)},5`);

  const submit = async () => {
    if (!nome.trim() || !telefono.trim()) {
      toast.error("Inserisci nome e telefono per ricevere il preventivo dettagliato.");
      return;
    }
    if (!privacy || !letto) {
      toast.error("Devi accettare l'informativa privacy per proseguire.");
      return;
    }
    try {
      await api.post("/quote", {
        tipo_immobile: immobile,
        metratura: metratura[0],
        tipo_intervento: TIPI_INTERVENTO[intervento].label,
        numero_bagni: bagni[0],
        cucina,
        extra: extras.map((id) => EXTRAS.find((e) => e.id === id).label),
        stima_min: min,
        stima_max: max,
        tempi_mesi: mesi,
        nome,
        telefono,
      });
      setSent(true);
      toast.success("Richiesta inviata! Ti contatteremo con un preventivo dettagliato.");
    } catch {
      toast.error("Si è verificato un errore. Riprova o scrivici su WhatsApp.");
    }
  };

  return (
    <section id="preventivo" className="py-24 md:py-32 bg-brand-ink grain relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="max-w-2xl mb-14">
          <span className="text-brand-terracotta font-semibold tracking-[0.2em] uppercase text-sm">Preventivo online</span>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-white mt-4 mb-5">
            Scopri quanto costa la tua ristrutturazione
          </h2>
          <p className="text-white/60 text-lg">
            Una stima immediata e trasparente. Il preventivo definitivo viene confermato dopo il sopralluogo gratuito.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Controls */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <div className="space-y-9">
              <div>
                <label className="block font-display font-bold text-brand-ink mb-3">Tipo di immobile</label>
                <Select value={immobile} onValueChange={setImmobile}>
                  <SelectTrigger data-testid="calc-immobile-select" className="h-12 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IMMOBILI.map((i) => (
                      <SelectItem key={i} value={i} data-testid={`immobile-${i}`}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-display font-bold text-brand-ink">Metratura</label>
                  <span data-testid="calc-metratura-value" className="text-brand-terracotta font-bold text-lg">{metratura[0]} m²</span>
                </div>
                <Slider data-testid="calc-metratura-slider" value={metratura} onValueChange={setMetratura} min={20} max={400} step={5} />
              </div>

              <div>
                <label className="block font-display font-bold text-brand-ink mb-3">Tipo di intervento</label>
                <Select value={intervento} onValueChange={setIntervento}>
                  <SelectTrigger data-testid="calc-intervento-select" className="h-12 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPI_INTERVENTO).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`intervento-${k}`}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between items-baseline mb-3">
                    <label className="font-display font-bold text-brand-ink">Bagni</label>
                    <span data-testid="calc-bagni-value" className="text-brand-terracotta font-bold text-lg">{bagni[0]}</span>
                  </div>
                  <Slider data-testid="calc-bagni-slider" value={bagni} onValueChange={setBagni} min={0} max={5} step={1} />
                </div>
                <div>
                  <label className="block font-display font-bold text-brand-ink mb-3">Cucina da rifare?</label>
                  <div className="flex gap-2">
                    {[true, false].map((v) => (
                      <button
                        key={String(v)}
                        data-testid={`calc-cucina-${v ? "si" : "no"}`}
                        onClick={() => setCucina(v)}
                        className={`flex-1 h-11 rounded-lg font-semibold text-sm transition-colors ${
                          cucina === v ? "bg-brand-ink text-white" : "bg-gray-100 text-brand-slate hover:bg-gray-200"
                        }`}
                      >
                        {v ? "Sì" : "No"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block font-display font-bold text-brand-ink mb-3">Extra (opzionali)</label>
                <div className="grid grid-cols-2 gap-3">
                  {EXTRAS.map((e) => (
                    <button
                      key={e.id}
                      data-testid={`calc-extra-${e.id}`}
                      onClick={() => toggleExtra(e.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-semibold text-left transition-colors ${
                        extras.includes(e.id)
                          ? "border-brand-terracotta bg-brand-terracotta/10 text-brand-terracottaDark"
                          : "border-gray-200 text-brand-slate hover:border-gray-300"
                      }`}
                    >
                      <span className="w-5 h-5 rounded border-2 border-brand-ink bg-white flex items-center justify-center flex-shrink-0">
                        {extras.includes(e.id) && <Check size={14} strokeWidth={3.5} className="text-brand-ink" />}
                      </span>
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sticky estimate */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-28 bg-white/5 border border-white/10 backdrop-blur-lg rounded-2xl p-8">
              <div className="flex items-center gap-2 text-brand-sage mb-6">
                <Calculator size={20} />
                <span className="font-semibold uppercase tracking-wider text-sm">Stima indicativa</span>
              </div>
              <div className="mb-2 text-white/50 text-sm">Investimento stimato</div>
              <motion.div
                key={`${min}-${max}`}
                initial={{ opacity: 0.4, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="calc-estimate"
                className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight mb-1"
              >
                {euro(min)}
              </motion.div>
              <div className="text-white/60 font-semibold mb-6">— {euro(max)}</div>

              <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-6">
                <div className="flex items-center gap-2 text-brand-sage mb-1.5">
                  <Clock size={16} />
                  <span className="font-semibold uppercase tracking-wider text-xs">Durata cantiere stimata</span>
                </div>
                <div data-testid="calc-tempi" className="font-display text-2xl font-extrabold text-white">
                  {fmtMesi(mesi)} mesi
                </div>
              </div>

              <div className="h-px bg-white/10 my-6" />

              {!sent ? (
                <div className="space-y-3">
                  <div className="space-y-2.5 mb-1">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox
                        data-testid="calc-privacy"
                        checked={privacy}
                        onCheckedChange={setPrivacy}
                        className="mt-0.5 border-white/50 data-[state=checked]:bg-brand-gold data-[state=checked]:text-brand-ink data-[state=checked]:border-brand-gold"
                      />
                      <span className="text-[11px] text-white/60 leading-relaxed">
                        Acconsento al trattamento dei dati personali ai sensi del Reg. UE 2016/679, come descritto nella{" "}
                        <a href="/privacy" target="_blank" rel="noreferrer" data-testid="calc-privacy-link" className="text-brand-gold font-semibold underline">Privacy Policy</a>. *
                      </span>
                    </label>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <Checkbox
                        data-testid="calc-letto"
                        checked={letto}
                        onCheckedChange={setLetto}
                        className="mt-0.5 border-white/50 data-[state=checked]:bg-brand-gold data-[state=checked]:text-brand-ink data-[state=checked]:border-brand-gold"
                      />
                      <span className="text-[11px] text-white/60 leading-relaxed">
                        Ho letto l'informativa{" "}
                        <a href="/privacy" target="_blank" rel="noreferrer" data-testid="calc-letto-link" className="text-brand-gold font-semibold underline">Privacy</a>. *
                      </span>
                    </label>
                  </div>
                  <Input
                    data-testid="calc-nome-input"
                    placeholder="Il tuo nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                  <Input
                    data-testid="calc-telefono-input"
                    placeholder="Telefono"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                  <button
                    data-testid="calc-submit-btn"
                    onClick={submit}
                    className="w-full bg-brand-gold hover:bg-brand-goldDark text-brand-ink font-bold py-3 rounded-full transition-colors text-sm leading-snug"
                  >
                    Richiedi subito il sopralluogo per un preventivo reale
                  </button>
                  <p className="text-white/40 text-xs text-center">Sopralluogo e preventivo gratuiti, senza impegno.</p>
                </div>
              ) : (
                <div data-testid="calc-success" className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-brand-sage/20 flex items-center justify-center mx-auto mb-4">
                    <Check className="text-brand-sage" size={28} />
                  </div>
                  <p className="text-white font-semibold mb-1">Richiesta ricevuta!</p>
                  <p className="text-white/60 text-sm">Ti contatteremo al più presto per il sopralluogo gratuito.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
